'use client'

import { AppDispatch, RootState } from '@/app/globalRedux/store'
import { useTranslation } from '@/app/i18n/client'
import {
  activeUsersList,
  advertisingList,
  buyersAlertPublicationList,
  companyEmailContactEmailList,
  contactTelList,
  guarantyAlertList,
  introToGoldPlatinum,
  noOfProductsList,
  onlineAccessList,
  printDirectoryList,
  priorityInResultPageList,
  prioritySupportList,
  profileInSelfManagementList,
  profilePrintDirectory,
  profileVisitorsList,
  profileWebsiteVisibleForList,
  salesOfferPublicationList,
  seeBaWithoutCompanyAndDetailsList,
  seeBuyerAlertList,
  solutionPlans,
  timeToDeliverBaList,
  viewOnCompanyProfileList,
} from '@/app/utils/pricingData'
import { getSubscriptionsList } from '@/services/generalDataServices'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import TableCell, {
  TableHeadRow,
  TableSubHeader,
  TableSubHeaderWithData,
} from './CommonComponents'

export const memberShips: Record<string, string> = {
  'Free Member': 'Free',
  'Silver Member': 'Silver',
  'Gold Member': 'Gold',
  'Platinum Member': 'Platinum',
}

const INTRO_TO = {
  for_buyers: 'Introductions to Gold and Platinum suppliers',
  for_sellers: 'Introductions to VIP buyers',
  for_prepost_harvest: 'Introductions to VIP members',
} as const

type INTRO_TYPE = keyof typeof INTRO_TO

const DeskTopSolutionsTable = () => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()
  const pathName = usePathname()
  const [plan, setPlan] = useState<string>('Free Member')
  const subsrciptionPage: INTRO_TYPE = ((): INTRO_TYPE => {
    const page = pathName.split('/').pop()
    if (
      page === 'for_buyers' ||
      page === 'for_sellers' ||
      page === 'for_prepost_harvest'
    ) {
      return page
    }
    return 'for_buyers'
  })()

  const handlePlanChange = (newPlan: string) => {
    setPlan(newPlan)
  }
  const selectedPlanIndex = solutionPlans.findIndex((p) => p.name === plan) + 1

  const { subscriptionsList } = useSelector((state: RootState) => {
    return state.constantData
  })
  useEffect(() => {
    if (subscriptionsList?.length <= 0) {
      dispatch(getSubscriptionsList())
    }
  }, [])

  return (
    <div className="">
      <div className="py-[20px] justify-center items-center bg-[#f1f1f1] mobile:flex hidden">
        <label htmlFor="planSelect" className="text-[14px] font-bold">
          {t('Select Plan')}
        </label>
        <span className="mx-1">:</span>
        <select
          id="planSelect"
          value={plan}
          onChange={(e) => handlePlanChange(e.target.value)}
          className="p-[5px] rounded border"
        >
          {Object.keys(memberShips).map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>
      </div>
      <table className="shadow-[0_0_14px_0_rgba(0,0,0,0.12)] w-[1280px] mobile:w-auto rounded-[10px]">
        <thead>
          <TableHeadRow plan={plan} subscriptionsList={subscriptionsList} />
        </thead>
        <tbody>
          <TableSubHeader plan={plan} title={t('REGISTRATION-LISTING')} />
          <tr className="borderBottom">
            {profilePrintDirectory.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {profileWebsiteVisibleForList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {priorityInResultPageList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <TableSubHeader plan={plan} title={t('MY FRUCTIDOR')} />
          <tr className="borderBottom">
            {profileInSelfManagementList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {profileVisitorsList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <TableSubHeaderWithData
            plan={plan}
            title={t('PRINT DIRECTORIES')}
            data={printDirectoryList}
          />
          <TableSubHeaderWithData
            plan={plan}
            title={t('ONLINE ACCESS DATABASE')}
            data={onlineAccessList}
          />
          <tr className="borderBottom">
            {activeUsersList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {viewOnCompanyProfileList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {contactTelList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {companyEmailContactEmailList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <TableSubHeader plan={plan} title={t('SALES OFFERS')} />
          <tr className="borderBottom">
            {salesOfferPublicationList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <TableSubHeader plan={plan} title={t('BUYERS ALERTS FOR A BUYER')} />
          <tr className="borderBottom">
            {buyersAlertPublicationList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {timeToDeliverBaList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {noOfProductsList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {seeBuyerAlertList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {seeBaWithoutCompanyAndDetailsList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <tr className="borderBottom">
            {guarantyAlertList.map((cell, index) => (
              <TableCell
                className={`${index === 0 || index === selectedPlanIndex ? '' : 'mobile:hidden'}`}
                key={index}
                {...cell}
                columnIndex={index}
              />
            ))}
          </tr>
          <TableSubHeaderWithData
            plan={plan}
            title={t(INTRO_TO[subsrciptionPage] ?? INTRO_TO['for_buyers'])}
            data={introToGoldPlatinum}
          />
          <TableSubHeaderWithData
            plan={plan}
            title={t('Priority support')}
            data={prioritySupportList}
          />
          <TableSubHeaderWithData
            plan={plan}
            title={t('Advertising')}
            data={advertisingList}
          />
        </tbody>
      </table>
    </div>
  )
}

export default DeskTopSolutionsTable
